import type {
  WatchAiSuggestion,
  WatchCompany,
  WatchIdentifier,
  WatchInvestigationCollection,
  WatchInvestigationFieldChange,
  WatchInvestigationRowChange,
} from './types';

const IDENTITY_FILL_KEYS = [
  'code',
  'website',
  'notes',
  'aliases',
  'ownership',
  'dateOpened',
  'dateClosed',
  'naceSection',
  'naceCode',
  'euTaxonomyCode',
  'addressState',
  'addressCity',
  'addressPostCode',
  'addressStreet',
  'addressBuilding',
  'addressCoordinates',
] as const;

const PATCH_SKIP_FIELDS = new Set(['action', 'id', 'reason', 'name', 'personFullName']);

export type SuggestionCompanySnapshot = Pick<
WatchCompany,
| 'code'
| 'website'
| 'notes'
| 'aliases'
| 'ownership'
| 'dateOpened'
| 'dateClosed'
| 'naceSection'
| 'naceCode'
| 'euTaxonomyCode'
| 'addressState'
| 'addressCity'
| 'addressPostCode'
| 'addressStreet'
| 'addressBuilding'
| 'addressCoordinates'
| 'identifiers'
| 'products'
| 'buildings'
| 'orgNodes'
| 'associations'
| 'financials'
>;

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function text(value: unknown): string {
  if (typeof value === 'string') {
    return value.trim();
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return '';
}

function dossierId(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }
  }
  return null;
}

export function suggestionRowHasDisplay(row: Record<string, unknown>, currentIds: Set<number>): boolean {
  if (text(row.name)
    || text(row.personFullName)
    || text(row.eventKind)
    || text(row.periodEnd)
    || text(row.scheme)) {
    return true;
  }
  const id = dossierId(row.id);
  return id != null && currentIds.has(id);
}

function currentValue(company: SuggestionCompanySnapshot, path: string): unknown {
  return company[path as keyof SuggestionCompanySnapshot];
}

function isEmptyCurrent(value: unknown): boolean {
  if (value == null) {
    return true;
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  return String(value).trim() === '';
}

function identityAfter(value: unknown, path: string): string {
  if (path === 'aliases' && Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean).join(', ');
  }
  return text(value);
}

function collectionAction(row: Record<string, unknown>): 'add' | 'patch' | 'delete' | null {
  const action = text(row.action);
  if (action === 'add' || action === 'patch' || action === 'delete') {
    return action;
  }
  return null;
}

function addKey(row: Record<string, unknown>, index: number): string {
  return text(row.name)
    || text(row.personFullName)
    || [text(row.eventKind), text(row.occurredOn)].filter(Boolean).join(':')
    || text(row.periodEnd)
    || text(row.scheme)
    || `add-${index}`;
}

function patchedFields(row: Record<string, unknown>): string[] {
  return Object.keys(row).filter((key) => !PATCH_SKIP_FIELDS.has(key) && row[key] != null && row[key] !== '');
}

function mapCollectionRows(
  rawRows: unknown,
  collection: WatchInvestigationCollection,
  currentIds: Set<number>,
): WatchInvestigationRowChange[] {
  const changes: WatchInvestigationRowChange[] = [];
  asArray(rawRows).forEach((item, index) => {
    const row = asRecord(item);
    if (!row || !suggestionRowHasDisplay(row, currentIds)) {
      return;
    }
    const action = collectionAction(row) || 'add';
    const id = dossierId(row.id);
    if (action === 'delete') {
      if (id == null) {
        return;
      }
      changes.push({
        collection,
        op: 'remove',
        key: `id:${id}`,
        before: row,
        after: null,
        reason: text(row.reason) || undefined,
      });
      return;
    }
    if (action === 'patch') {
      if (id == null) {
        return;
      }
      changes.push({
        collection,
        op: 'update',
        key: `id:${id}`,
        before: null,
        after: row,
        patchedFields: patchedFields(row),
        reason: text(row.reason) || undefined,
      });
      return;
    }
    changes.push({
      collection,
      op: 'add',
      key: addKey(row, index),
      before: null,
      after: row,
    });
  });
  return changes;
}

function identifierChanges(
  rawRows: unknown,
  current: WatchIdentifier[],
): WatchInvestigationFieldChange[] {
  const known = new Set(current.map((item) => item.scheme.toLowerCase()));
  const changes: WatchInvestigationFieldChange[] = [];
  asArray(rawRows).forEach((item) => {
    const row = asRecord(item);
    if (!row) {
      return;
    }
    const scheme = text(row.scheme);
    const value = text(row.value);
    if (!scheme || !value || known.has(scheme.toLowerCase())) {
      return;
    }
    changes.push({
      path: `identifier.${scheme}`,
      before: '',
      after: `${scheme}:${value}`,
    });
  });
  return changes;
}

function identityChanges(
  rawCompany: unknown,
  company: SuggestionCompanySnapshot,
): WatchInvestigationFieldChange[] {
  const row = asRecord(rawCompany);
  if (!row) {
    return [];
  }
  return IDENTITY_FILL_KEYS.flatMap((path) => {
    if (!(path in row) || row[path] == null) {
      return [];
    }
    if (!isEmptyCurrent(currentValue(company, path))) {
      return [];
    }
    const after = identityAfter(row[path], path);
    if (!after) {
      return [];
    }
    return [{ path, before: '', after }];
  });
}

function isLegacySuggestion(row: Record<string, unknown>): boolean {
  return Array.isArray(row.fieldChanges) && Array.isArray(row.rowChanges);
}

function isLambdaSuggestion(row: Record<string, unknown>): boolean {
  return row.company != null
    || Array.isArray(row.identifiers)
    || Array.isArray(row.products)
    || Array.isArray(row.buildings)
    || Array.isArray(row.orgNodes)
    || Array.isArray(row.associations)
    || Array.isArray(row.financials);
}

export function normalizeWatchAiSuggestion(
  raw: unknown,
  company: SuggestionCompanySnapshot,
): WatchAiSuggestion | null {
  const row = asRecord(raw);
  if (!row) {
    return null;
  }
  if (isLegacySuggestion(row)) {
    return row as unknown as WatchAiSuggestion;
  }
  if (!isLambdaSuggestion(row)) {
    return null;
  }
  const fieldChanges = [
    ...identityChanges(row.company, company),
    ...identifierChanges(row.identifiers, company.identifiers),
  ];
  const rowChanges = [
    ...mapCollectionRows(row.products, 'products', new Set(company.products.map((item) => item.id))),
    ...mapCollectionRows(row.buildings, 'buildings', new Set(company.buildings.map((item) => item.id))),
    ...mapCollectionRows(row.orgNodes, 'orgNodes', new Set(company.orgNodes.map((item) => item.id))),
    ...mapCollectionRows(
      row.associations,
      'associations',
      new Set(company.associations.map((item) => item.id)),
    ),
    ...mapCollectionRows(row.financials, 'financials', new Set(company.financials.map((item) => item.id))),
  ];
  return { fieldChanges, rowChanges };
}
